import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useMap } from "react-leaflet";
import L from "leaflet";
import InfoCardFrame from "@/components/map/InfoCardFrame";
import { placeInfoCard } from "@/components/map/infoCardPlacement";
import { useLayoutBounds } from "@/features/layout/layoutBounds";

const POINTER_SIZE = 12;
const HEADER_HEIGHT = 44;

/**
 * A floating card anchored to a map lat/lng position.
 * Survives map drags, supports dragging the card itself, and blocks
 * map interaction when hovered.
 *
 * @param {object} props
 * @param {{lat: number, lng: number}} props.anchorLatLng - Map position to anchor to
 * @param {function} props.onClose - Called when close button is clicked
 * @param {string} props.title - Card header title
 * @param {string} [props.subtitle] - Optional subtitle below title
 * @param {React.ReactNode} [props.icon] - Optional icon in header
 * @param {number} [props.width=280] - Card width
 * @param {React.ReactNode} [props.actions] - Extra header buttons before drag/close
 * @param {React.ReactNode} props.children - Card body content
 */
export default function MapInfoCard({
  anchorLatLng,
  onClose,
  title,
  subtitle,
  icon,
  width = 280,
  headerColor,
  actions = null,
  children,
}) {
  const map = useMap();
  const cardRef = useRef(null);
  const draggingRef = useRef(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  // null until the user drags the card; then the card keeps that offset to its anchor.
  const [dragOffset, setDragOffset] = useState(null);
  const [size, setSize] = useState({ w: width, h: 160 });
  const [viewportTick, setViewportTick] = useState(0);
  const bounds = useLayoutBounds();

  // Measured before paint, else a card near the bottom edge is first drawn with a stale height and jumps.
  useLayoutEffect(() => {
    const el = cardRef.current;
    if (el && (el.offsetWidth !== size.w || el.offsetHeight !== size.h)) {
      setSize({ w: el.offsetWidth, h: el.offsetHeight });
    }
  });

  useEffect(() => {
    const el = cardRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() =>
      setSize({ w: el.offsetWidth, h: el.offsetHeight }),
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Update position on map move/zoom
  useEffect(() => {
    if (!map) return;
    const onMove = () => setViewportTick((t) => t + 1);
    map.on("move", onMove);
    map.on("zoom", onMove);
    return () => {
      map.off("move", onMove);
      map.off("zoom", onMove);
    };
  }, [map]);

  // Block map interaction when hovering over card
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    L.DomEvent.disableClickPropagation(el);
  }, [title, anchorLatLng]);

  // Reset drag offset when anchor changes
  const anchorKey = `${anchorLatLng?.lat ?? "na"}-${anchorLatLng?.lng ?? "na"}`;
  useLayoutEffect(() => {
    setDragOffset(null);
  }, [anchorKey]);

  const placement = useMemo(() => {
    const lat = Number(anchorLatLng?.lat);
    const lng = Number(anchorLatLng?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return { style: { top: 74, right: 8 }, pointer: null };
    }
    const pt = map.latLngToContainerPoint([lat, lng]);
    if (dragOffset) {
      return { style: { left: pt.x + dragOffset.x, top: pt.y + dragOffset.y }, pointer: null };
    }
    // layoutBounds are viewport pixels; the card is positioned inside the map container.
    const rect = map.getContainer().getBoundingClientRect();
    const area = {
      left: Math.max(bounds.left, bounds.leftColumnRight ?? 0) - rect.left,
      right: Math.min(bounds.right, rect.right) - rect.left,
      top: Math.max(bounds.top, rect.top) - rect.top,
      bottom: Math.min(bounds.bottom, rect.bottom) - rect.top,
    };
    const placed = placeInfoCard({ anchor: pt, size, area });
    // Whole pixels: the rotated pointer shimmers on sub-pixel offsets.
    return {
      style: { left: Math.round(placed.left), top: Math.round(placed.top) },
      pointer: placed.pointer && { ...placed.pointer, y: Math.round(placed.pointer.y) },
    };
  }, [map, anchorLatLng?.lat, anchorLatLng?.lng, dragOffset, size, bounds, viewportTick]);
  const cardStyle = placement.style;

  const headerActions = (
    <>
      {actions}
      <IconButton
        size="small"
        sx={{ color: "#004f7d", cursor: "grab", pointerEvents: "auto" }}
        onPointerDown={(e) => {
          e.stopPropagation();
          e.preventDefault();
          e.currentTarget.setPointerCapture?.(e.pointerId);
          draggingRef.current = true;
          map?.dragging?.disable?.();
          const pt = map.latLngToContainerPoint([anchorLatLng.lat, anchorLatLng.lng]);
          const offset = dragOffset ?? {
            x: cardStyle.left - pt.x,
            y: cardStyle.top - pt.y,
          };
          dragStartRef.current = {
            x: offset.x - e.clientX,
            y: offset.y - e.clientY,
          };
        }}
        onPointerMove={(e) => {
          if (!draggingRef.current) return;
          e.stopPropagation();
          setDragOffset({
            x: dragStartRef.current.x + e.clientX,
            y: dragStartRef.current.y + e.clientY,
          });
        }}
        onPointerUp={(e) => {
          e.stopPropagation();
          draggingRef.current = false;
          map?.dragging?.enable?.();
          e.currentTarget.releasePointerCapture?.(e.pointerId);
        }}
        onPointerCancel={(e) => {
          e.stopPropagation();
          draggingRef.current = false;
          map?.dragging?.enable?.();
          e.currentTarget.releasePointerCapture?.(e.pointerId);
        }}
      >
        <DragIndicatorIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={onClose}
        sx={{ color: "#004f7d", pointerEvents: "auto" }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </>
  );

  const pointer = placement.pointer;
  return (
    <>
    {pointer && (
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          zIndex: 951,
          pointerEvents: "none",
          width: POINTER_SIZE,
          height: POINTER_SIZE,
          left:
            pointer.side === "right"
              ? cardStyle.left - POINTER_SIZE / 2
              : cardStyle.left + size.w - POINTER_SIZE / 2,
          top: cardStyle.top + pointer.y - POINTER_SIZE / 2,
          transform: "rotate(45deg)",
          background:
            pointer.y < HEADER_HEIGHT && headerColor
              ? `linear-gradient(${headerColor}1A, ${headerColor}1A), #fff`
              : "#fff",
          border: "1px solid rgba(0,79,125,0.16)",
          ...(pointer.side === "right"
            ? { borderTopColor: "transparent", borderRightColor: "transparent" }
            : { borderBottomColor: "transparent", borderLeftColor: "transparent" }),
        }}
      />
    )}
    <InfoCardFrame
      ref={cardRef}
      title={title}
      subtitle={subtitle}
      icon={icon}
      width={width}
      headerColor={headerColor}
      headerActions={headerActions}
      sx={{
        position: "absolute",
        zIndex: 950,
        pointerEvents: "auto",
        ...cardStyle,
      }}
      onPointerDown={(e) => e.stopPropagation()}
      onPointerMove={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
    >
      {children}
    </InfoCardFrame>
    </>
  );
}
